import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckIcon, XIcon, InfoIcon, AlertTriangleIcon } from '../icons';

/**
 * Toast Component - Clean card design with colored accent strip
 */

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let toastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((type: ToastType, message: string, duration = 3000) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, type, message, duration }]);

    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const value: ToastContextType = {
    showToast,
    success: (msg) => showToast('success', msg),
    error: (msg) => showToast('error', msg),
    warning: (msg) => showToast('warning', msg),
    info: (msg) => showToast('info', msg),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

// Toast Container
function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: number) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-8 left-6 right-6 z-[200] flex flex-col gap-3 pointer-events-none items-center">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => onRemove(toast.id)} />
      ))}
    </div>
  );
}

// Individual Toast
function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const styles = {
    success: {
      accent: 'bg-emerald-500',
      icon: <CheckIcon size={18} className="text-emerald-500" />,
      bg: 'bg-white dark:bg-slate-900',
    },
    error: {
      accent: 'bg-rose-500',
      icon: <XIcon size={18} className="text-rose-500" />,
      bg: 'bg-white dark:bg-slate-900',
    },
    warning: {
      accent: 'bg-amber-500',
      icon: <AlertTriangleIcon size={18} className="text-amber-500" />,
      bg: 'bg-white dark:bg-slate-900',
    },
    info: {
      accent: 'bg-sky-500',
      icon: <InfoIcon size={18} className="text-sky-500" />,
      bg: 'bg-white dark:bg-slate-900',
    },
  };

  const { accent, icon, bg } = styles[toast.type];

  return (
    <div
      className={`
        ${bg} pl-1 pr-4 py-3 rounded-xl
        flex items-center gap-3 pointer-events-auto
        animate-slide-in border border-slate-200 dark:border-slate-800
        max-w-md w-full sm:w-auto shadow-sm overflow-hidden
        relative group
      `}
    >
      {/* Colored accent strip */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
      
      <div className="flex-shrink-0 ml-3 w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
        {icon}
      </div>
      
      <p className="flex-1 font-semibold text-sm text-slate-700 dark:text-slate-200 leading-tight">
        {toast.message}
      </p>
      
      <button
        onClick={onRemove}
        className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <XIcon size={16} />
      </button>
    </div>
  );
}
